import React, { useCallback, useState } from 'react';
import { Input } from '@/components/ui/input';
import { useFormContext, Controller } from 'react-hook-form';
import { QuestionData } from '../types';
import { useParams } from 'react-router-dom';
import { useClarificationsContext } from '@/contexts/ClarificationsContext';
import { useMultiUploadDocuments } from '@/hooks/submission_forms/useMultiUploadDocuments';
import type { Document } from '@/hooks/submissions/submissionDocumentApi';
import DocumentPreviewModal, {
  openDocument,
  type PreviewType,
} from '../DocumentPreviewModal';
import { X } from 'lucide-react';
import {
  UploadCompleteBadge,
  UploadProgressIndicator,
} from './UploadProgressIndicator';

interface MultiUploadFieldProps {
  question: QuestionData;
  disabled?: boolean;
}

export const MultiUploadField: React.FC<MultiUploadFieldProps> = ({
  question,
  disabled,
}) => {
  const { control, setValue } = useFormContext();
  const { submissionId } = useParams();
  const { refetch: refetchClarifications } = useClarificationsContext();
  const [isOpening, setIsOpening] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewType, setPreviewType] = useState<PreviewType | null>(null);
  const [previewFile, setPreviewFile] = useState<Document | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const syncFilenames = useCallback(
    (filenames: string[]) => {
      setValue(question.slug, filenames, { shouldValidate: true });
    },
    [question.slug, setValue]
  );

  const {
    files,
    isLoading,
    error,
    uploadFiles,
    removeFile,
    isUploading,
    uploadProgress,
    uploadStatus,
    recentlyUploadedIds,
    removingId,
  } = useMultiUploadDocuments({
    submissionId,
    questionSlug: question.slug,
    syncFilenames,
    onMutationSuccess: refetchClarifications,
  });

  const inputDisabled = disabled || isLoading || isUploading;

  async function upload(selectedFiles: FileList | null) {
    if (!selectedFiles?.length) return;

    try {
      await uploadFiles(Array.from(selectedFiles));
    } catch {
      // Error surfaced via hook state
    }
  }

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const input = e.target;
    await upload(input.files);
    input.value = '';
  }

  function handleDragOver(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    if (inputDisabled) {
      e.dataTransfer.dropEffect = 'none';
      return;
    }
    e.dataTransfer.dropEffect = 'copy';
    setIsDragging(true);
  }

  function handleDragLeave(e: React.DragEvent<HTMLDivElement>) {
    // Moving onto a child element fires dragleave on the zone; only clear the
    // highlight once the pointer actually leaves it.
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    setIsDragging(false);
  }

  async function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDragging(false);
    if (inputDisabled) return;
    await upload(e.dataTransfer.files);
  }

  async function handleRemove(documentId: string) {
    try {
      await removeFile(documentId);
    } catch {
      // Error surfaced via hook state
    }
  }

  async function handleOpen(file: Document) {
    if (!file.view_url) return;
    setIsOpening(file.id);
    try {
      const result = openDocument(file.filename, file.view_url);
      if (result) {
        setPreviewFile(file);
        setPreviewType(result);
        setPreviewOpen(true);
      }
    } finally {
      setIsOpening(null);
    }
  }

  return (
    <div>
      {files.length > 0 && (
        <ul className="mb-2 space-y-1">
          {files.map(file => (
            <li key={file.id} className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => handleOpen(file)}
                disabled={isOpening === file.id}
                className="text-sm text-primary hover:underline text-left disabled:opacity-50"
              >
                {isOpening === file.id ? 'Opening...' : file.filename}
              </button>
              {recentlyUploadedIds.includes(file.id) && <UploadCompleteBadge />}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => handleRemove(file.id)}
                  disabled={removingId === file.id}
                  className="print:hidden text-gray-400 hover:text-red-500 disabled:opacity-50"
                  aria-label={`Remove ${file.filename}`}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <div
        data-testid={`${question.slug}-dropzone`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`rounded-md border-2 border-dashed p-3 transition-colors ${
          isDragging ? 'border-primary bg-primary/5' : 'border-input'
        }`}
      >
        <Controller
          name={question.slug}
          control={control}
          render={() => (
            <Input
              type="file"
              multiple
              className="w-full cursor-pointer file:cursor-pointer"
              onChange={handleUpload}
              disabled={inputDisabled}
            />
          )}
        />
        {!disabled && !uploadProgress && (
          <p className="print:hidden mt-2 text-sm text-muted-foreground">
            Drag and drop files here, or choose files
          </p>
        )}
        <UploadProgressIndicator
          status={uploadStatus}
          batch={
            uploadProgress && {
              current: Math.min(uploadProgress.done + 1, uploadProgress.total),
              total: uploadProgress.total,
            }
          }
        />
      </div>
      {previewType && previewFile && (
        <DocumentPreviewModal
          open={previewOpen}
          onClose={() => {
            setPreviewOpen(false);
            setPreviewFile(null);
            setPreviewType(null);
          }}
          fileName={previewFile.filename}
          viewUrl={previewFile.view_url ?? ''}
          previewType={previewType}
        />
      )}
      {error && <div className="text-red-500">{error}</div>}
    </div>
  );
};
